/**
 * StarNetwork — distant pulsing stars behind the Spark.
 *
 * Star count scales with cumulative taps (see `docs/16_random_sensory_effects.md`
 * section 2.3 — "the network grows as you tap"):
 *   - 0..100   taps → 2 stars (distant, almost alone)
 *   - 100..500 → 7
 *   - 500..2000 → 15
 *   - 2000..10000 → 30
 *   - 10000+ → 60
 *
 * Each star has a deterministic position based on its index so the
 * background does not jitter between renders. Pulse delays are also
 * deterministic per index, so two stars never breathe in phase.
 *
 * Twinkle effect: one star at random gets a brief brighter pulse when
 * the random-events scheduler emits `bg_star_twinkle`.
 */
import { useMemo } from 'react';
import { useGameStore } from '../stores/game.js';
import { useRandomEvent } from '../random-events/useRandomEvent.js';
import styles from './StarNetwork.module.css';

interface StarSpec {
  readonly id: number;
  readonly leftPct: number; // 0..100
  readonly topPct: number; // 0..100
  readonly pulsePeriodSec: number; // 5..15
  readonly pulseDelaySec: number; // 0..pulsePeriodSec
  readonly sizePx: number; // 4..10
}

/** Deterministic PRNG (Mulberry32) for stable star positions. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * 2026-05-26 perf: половина звёзд на узких viewport'ах (mobile <= 600px).
 * Каждая звезда = independent CSS animation slot в compositor. 60 звёзд
 * на iPhone — 60 active animation layers = заметный jank в TG bot.
 * Cap'нули мобильное состояние, на desktop оставили полный рост.
 */
function isNarrowViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 600;
}

function starCountForTaps(taps: number, mobile: boolean): number {
  if (mobile) {
    if (taps < 100) return 2;
    if (taps < 500) return 5;
    if (taps < 2000) return 10;
    if (taps < 10000) return 18;
    return 25; // mobile cap — было 60
  }
  if (taps < 100) return 2;
  if (taps < 500) return 7;
  if (taps < 2000) return 15;
  if (taps < 10000) return 30;
  return 60;
}

function generateStars(count: number): readonly StarSpec[] {
  const rng = mulberry32(0xc0ffee);
  const stars: StarSpec[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      id: i,
      // Keep stars away from the dead center (where the Spark lives)
      // by mapping the random value into two bands. The Spark zone is
      // roughly 35..65% horizontal and 30..60% vertical.
      leftPct: rng() < 0.5 ? rng() * 35 : 65 + rng() * 35,
      topPct: rng() < 0.5 ? rng() * 30 : 60 + rng() * 38,
      pulsePeriodSec: 5 + rng() * 10,
      pulseDelaySec: rng() * 15,
      sizePx: 4 + rng() * 6,
    });
  }
  return stars;
}

export function StarNetwork(): JSX.Element {
  const totalTaps = useGameStore((s) => s.state.stats.totalTaps);
  const activeEvent = useRandomEvent();

  // Mobile-aware cap (mobile = innerWidth<600px). Pre-compute once на mount —
  // re-resize десктопа в mobile не реалистичный сценарий, экономим listener.
  const mobile = useMemo(() => isNarrowViewport(), []);
  const count = starCountForTaps(totalTaps, mobile);
  // Generate max once — slice as count grows. Maximum mobile = 25, desktop = 60.
  const stars = useMemo(() => generateStars(mobile ? 25 : 60), [mobile]);
  const visibleStars = stars.slice(0, count);

  // If the active event is bg_star_twinkle, pick one star to twinkle.
  // We use the event's emittedAt timestamp as a stable seed so the same
  // star twinkles for the duration of this event (no re-roll on re-render).
  const twinklingStarId =
    activeEvent?.id === 'bg_star_twinkle' && visibleStars.length > 0
      ? Math.floor((activeEvent.emittedAt % 1000) / (1000 / visibleStars.length))
      : -1;

  return (
    <div className={styles.network} aria-hidden>
      {visibleStars.map((star) => {
        const isTwinkling = star.id === twinklingStarId;
        // `?? ''` satisfies noUncheckedIndexedAccess for CSS-module exports.
        const baseClass = styles.star ?? '';
        const twinkleClass = styles.twinkling ?? '';
        const className = isTwinkling ? `${baseClass} ${twinkleClass}` : baseClass;
        return (
          <div
            key={star.id}
            className={className}
            style={{
              left: `${star.leftPct.toString()}%`,
              top: `${star.topPct.toString()}%`,
              width: `${star.sizePx.toString()}px`,
              height: `${star.sizePx.toString()}px`,
              ['--pulse-period' as string]: `${star.pulsePeriodSec.toString()}s`,
              ['--pulse-delay' as string]: `${star.pulseDelaySec.toString()}s`,
            }}
          />
        );
      })}
    </div>
  );
}
